import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import { agentStepReceived, agentCompleteReceived } from '../../store/slices/agentProgressSlice';
import { useGetScanQuery, useListRefactorPlansQuery } from '../../store/api/githubApi';
import { useWebSocket, WsEvent } from '../../hooks/useWebSocket';
import type { RootState } from '../../store/store';
import type { Scan, Issue, RefactorPlan } from '../../store/api/githubApi';
import { SEVERITY_ORDER } from './constants';
import { worstSeverity } from './utils';

interface ScanContextValue {
  scanId: string;
  scan: Scan | undefined;
  issues: Issue[];
  byCategory: Record<string, Issue[]>;
  sortedFiles: [string, Issue[]][];
  perfIssues: Issue[];
  prs: { prUrl: string | null; prNumber: number | null; issues: Issue[]; refactorPlan?: RefactorPlan }[];
  fixingCount: number;
  isLoading: boolean;
  error: unknown;
  isRunning: boolean;
  refreshPolling: () => void;
  showSnackbar: (msg: string, action?: React.ReactNode) => void;
}

const ScanContext = createContext<ScanContextValue | null>(null);

export const useScan = (): ScanContextValue => {
  const ctx = useContext(ScanContext);
  if (!ctx) throw new Error('useScan must be used inside ScanLayout');
  return ctx;
};

interface ScanProviderProps {
  children: React.ReactNode;
  onSnackbar: (msg: string, action?: React.ReactNode) => void;
}

export const ScanProvider: React.FC<ScanProviderProps> = ({ children, onSnackbar }) => {
  const { scanId = '' } = useParams<{ scanId: string }>();
  const { user, accessToken } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [pollingInterval, setPollingInterval] = useState(3000);
  const onSnackbarRef = useRef(onSnackbar);
  onSnackbarRef.current = onSnackbar;

  const { data: scan, isLoading, error, refetch } = useGetScanQuery(Number(scanId), {
    skip: !scanId,
    pollingInterval,
  });
  const { data: refactorPlans = [] } = useListRefactorPlansQuery(scan?.repository_id ?? 0, {
    skip: !scan?.repository_id,
    pollingInterval,
  });

  React.useEffect(() => {
    if (!scan) return;
    const scanDone = ['complete', 'failed'].includes(scan.status);
    const fixInProgress = scan.issues?.some((i) => i.status === 'fix_generating') ?? false;
    const refactorInProgress = refactorPlans.some((p) =>
      ['generating', 'pr_generating', 'issue_generating', 'regenerating'].includes(p.status)
    );
    if (scanDone && !fixInProgress && !refactorInProgress) setPollingInterval(0);
    else if (scanDone && (fixInProgress || refactorInProgress)) setPollingInterval(5000);
  }, [scan, scan?.status, scan?.issues, refactorPlans]);

  const handleWsMessage = useCallback((e: WsEvent) => {
    // Agent events use plan_id, not scan_id — handle before the scan_id guard
    if (e.event === 'agent_step') {
      dispatch(agentStepReceived(e));
      return;
    }
    if (e.event === 'agent_complete') {
      dispatch(agentCompleteReceived(e));
      refetch();
      return;
    }

    if (!('scan_id' in e) || e.scan_id !== Number(scanId)) return;

    if (e.event === 'fix_started') {
      // Worker has marked issues as fix_generating — refetch immediately so the banner appears
      refetch();
    } else if (e.event === 'fix_complete') {
      refetch();
      setPollingInterval(0);
      const action = e.pr_url ? (
        <Button size="small" color="inherit" href={e.pr_url} target="_blank" rel="noopener noreferrer">
          View PR #{e.pr_number}
        </Button>
      ) : undefined;
      onSnackbarRef.current(
        e.pr_url
          ? `PR #${e.pr_number} raised — ${e.fixed_issue_ids.length} issue(s) fixed.`
          : 'Fix complete — no changes were committed.',
        action,
      );
    } else if (e.event === 'fix_failed') {
      refetch();
      onSnackbarRef.current(`Fix failed: ${e.error}`);
    }
  }, [scanId, refetch, dispatch]);

  useWebSocket({ userId: user?.id ?? null, token: accessToken, onMessage: handleWsMessage });

  const refreshPolling = useCallback(() => setPollingInterval(5000), []);

  const issues = React.useMemo(() => scan?.issues ?? [], [scan?.issues]);
  const fixingCount = React.useMemo(
    () => issues.filter((i) => i.status === 'fix_generating').length,
    [issues]
  );

  const byCategory = React.useMemo(() => {
    const acc: Record<string, Issue[]> = {};
    for (const issue of issues) {
      (acc[issue.category] = acc[issue.category] ?? []).push(issue);
    }
    return acc;
  }, [issues]);

  const byFile = React.useMemo(() => {
    const acc: Record<string, Issue[]> = {};
    for (const issue of issues) {
      (acc[issue.file_path] = acc[issue.file_path] ?? []).push(issue);
    }
    return acc;
  }, [issues]);

  const sortedFiles = React.useMemo(() => {
    // Pre-calculate worst severity per file once (O(N)) so the sort comparison is O(1)
    const fileSeverityMap = new Map<string, number>();
    for (const [filePath, fileIssues] of Object.entries(byFile)) {
      fileSeverityMap.set(filePath, SEVERITY_ORDER.indexOf(worstSeverity(fileIssues)));
    }

    return Object.entries(byFile).sort(([pathA, a], [pathB, b]) => {
      const wa = fileSeverityMap.get(pathA) ?? 99;
      const wb = fileSeverityMap.get(pathB) ?? 99;
      return wa !== wb ? wa - wb : b.length - a.length;
    });
  }, [byFile]);

  const perfIssues = React.useMemo(() => {
    return [...issues]
      .filter((i) => i.perf_estimated_compute_saving || i.perf_estimated_memory_saving)
      .sort((a, b) => (b.ai_priority_score ?? 0) - (a.ai_priority_score ?? 0));
  }, [issues]);

  const prs = React.useMemo(() => {
    const prMap = new Map<string, { prUrl: string | null; prNumber: number | null; issues: Issue[]; refactorPlan?: RefactorPlan }>();
    for (const issue of issues) {
      if (['pr_raised', 'pr_merged'].includes(issue.status) && (issue.pr_url || issue.pr_number)) {
        const key = issue.pr_url ?? String(issue.pr_number);
        let entry = prMap.get(key);
        if (!entry) {
          entry = { prUrl: issue.pr_url, prNumber: issue.pr_number, issues: [] };
          prMap.set(key, entry);
        }
        entry.issues.push(issue);
      }
    }
    for (const plan of refactorPlans) {
      if (['pr_raised', 'pr_merged'].includes(plan.status) && (plan.pr_url || plan.pr_number)) {
        const key = plan.pr_url ?? String(plan.pr_number);
        if (!prMap.has(key)) {
          prMap.set(key, { prUrl: plan.pr_url, prNumber: plan.pr_number, issues: [], refactorPlan: plan });
        }
      }
    }
    return Array.from(prMap.values()).sort((a, b) => (b.prNumber ?? 0) - (a.prNumber ?? 0));
  }, [issues, refactorPlans]);

  const isRunning = !['complete', 'failed'].includes(scan?.status ?? '');

  const contextValue = React.useMemo<ScanContextValue>(() => ({
    scanId,
    scan,
    issues,
    byCategory,
    sortedFiles,
    perfIssues,
    prs,
    fixingCount,
    isLoading,
    error,
    isRunning,
    refreshPolling,
    showSnackbar: onSnackbar,
  }), [
    scanId,
    scan,
    issues,
    byCategory,
    sortedFiles,
    perfIssues,
    prs,
    fixingCount,
    isLoading,
    error,
    isRunning,
    refreshPolling,
    onSnackbar,
  ]);

  return (
    <ScanContext.Provider value={contextValue}>
      {children}
    </ScanContext.Provider>
  );
};
